/**
 * Importing necessary modules and components.
 * 
 * React - A JavaScript library for building user interfaces.
 * useEffect - A React Hook that lets you perform side effects in function components.
 * Swiper - A modern mobile touch slider with hardware accelerated transitions.
 * "swiper/css/bundle" - Importing Swiper's CSS bundle.
 * useAboutContext - Custom hook to access About context.
 * useTestimonialContext - Custom hook to access Testimonial context.
 * useTrackingManagementContext - Custom hook to access Tracking Management context.
 * Link - A component from react-router-dom to create navigational links.
 */
import React, { useEffect } from "react";
import Swiper from "swiper/bundle";
import "swiper/css/bundle";
import { useAboutContext } from "../../../Context/Aboutapi";
import { useTestimonialContext } from "../../../Context/Testimonialapi";
import { useTrackingManagementContext } from "../../../Context/TrackingManagement";
import { Link } from "react-router-dom";
import { useScrollingManagementContext } from "../../../Context/ScrollingEventManagement";

/**
 * Testimonial component fetches and displays testimonials and about section details.
 * It initializes a Swiper instance for the testimonial slider and cleans it up on component unmount.
 * 
 * @component
 * @example
 * return (
 *   <Testimonial />
 * )
 * 
 * @returns {JSX.Element} The rendered component.
 * 
 * @hook
 * @name useAboutContext
 * @description Fetches about section details and handler.
 * 
 * @hook
 * @name useTestimonialContext
 * @description Fetches testimonials and handler.
 * 
 * @hook
 * @name useTrackingManagementContext
 * @description Fetches tracking ID.
 * 
 * @function
 * @name fetchAboutSectionByHomeHandler
 * @description Fetches about section details for the home page.
 * 
 * @function
 * @name fetchTestimonialHandler
 * @description Fetches testimonials.
 * 
 * @typedef {Object} Testimonial
 * @property {string} pic - The URL of the testimonial picture.
 * @property {string} testimonials - The testimonial text.
 * @property {string} name - The name of the person giving the testimonial.
 * 
 * @typedef {Object} AboutSectionDetails
 * @property {string} testimonialdescription - The description of the testimonial section.
 * 
 * @typedef {Object} TrackingManagementContext
 * @property {string} trackingid - The tracking ID.
 */
function Testimonial() {
  const {
    aboutsectionDetails,
    fetchAboutSectionByHomeHandler,
  } = useAboutContext();

  const { Testimonial, fetchTestimonialHandler } = useTestimonialContext();

  const { trackingid } = useTrackingManagementContext();
   // Extracting scrolling and tracking handlers
      const { ScrollingopenHandler } = useScrollingManagementContext();

  useEffect(() => {
    fetchAboutSectionByHomeHandler();
    fetchTestimonialHandler();

    // Swiper Slider Activation
    const swiper = new Swiper(".home-one-testimonial-activator", {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      pagination: false,
      grabCursor: true,
      speed: 1500,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false,
      },
      breakpoints: {
        577: {
          slidesPerView: 2,
        },
      },
    });

    return () => {
      if (swiper) swiper.destroy(); // Clean up Swiper instance on component unmount
    };
  }, []);

  return (
    <>
      <div
        className="testimonial-area-1 section-gap-equal pb-2"
        style={{ padding: "30px 0px" }}
      >
        <div className="container">
          <div className="row g-lg-5">
            <div className="col-lg-5">
              <div className="testimonial-heading-area">
                <div className="section-title section-left">
                  <span className="pre-title custom-font-family">
                    Testimonials
                  </span>
                  <h2 className="title custom-font-family">
                    What Our Students Have To Say
                  </h2>
                  <span className="shape-line">
                    <i className="icon-19"></i>
                  </span>
                  <p
                    className="custom-font-family"
                    dangerouslySetInnerHTML={{
                      __html: aboutsectionDetails.testimonialdescription,
                    }}
                  />
                  <Link
                    to="/testimonial-details"
                    id={trackingid}
                    data-button-id={
                      "Click on the button View All on the Home Page"
                    }
                    className="edu-btn btn-large custom-font-family text-white" 
                    onClick={() => ScrollingopenHandler()}
                  >
                    View All<i className="icon-4"></i>
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="home-one-testimonial-activator swiper ">
                <div className="swiper-wrapper">
                  {Testimonial.map((Test, index) => (
                    <div className="swiper-slide">
                      <div className="testimonial-grid">
                        <div className="thumbnail">
                          <img
                            src={Test.pic}
                            style={{ width: "80px" }}
                            alt="Testimonial"
                          />
                          <span className="qoute-icon">
                            <i className="icon-26"></i>
                          </span>
                        </div>
                        <div className="content">
                          <p className="custom-font-family">
                            {Test.testimonials}
                          </p>
                          <h6 className="title custom-font-family">
                            {Test.name}
                          </h6>
                          <span className="subtitle fs-4 custom-font-family">
                            Student
                          </span>
                          <div className="course-rating mt-2">
                            <div className="rating">
                              <i className="icon-23 fs-5 text-warning"></i>
                              <i className="icon-23 fs-5 text-warning"></i>
                              <i className="icon-23 fs-5 text-warning"></i>
                              <i className="icon-23 fs-5 text-warning"></i>
                              <i className="icon-23 fs-5 text-warning"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Testimonial;
